import type { SequenceAPI, SequenceMessage } from "$lib/types";

export class CampaignAPI {
  private static readonly BASE_URL = "/api/v1/campaigns";
  private static readonly BASE_URL_CAMPAIGN = "/api/v1/campaign";
  private static readonly BASE_URL_SEQUENCES = "/api/v1/sequences";
  private static readonly ENDPOINTS = {
    campaigns: "/campaigns",
    campaignBySlug: "/campaign",
    userCampaigns: "/user_campaigns",
    deleteCampaign: "/delete_campaign",
    updateCampaign: "/update_campaign",
  } as const;

  private static readonly HEADERS = {
    contentType: "Content-Type",
    jsonContentType: "application/json",
  } as const;

  static async getCampaigns() {
    try {
      const response = await fetch(`${this.BASE_URL}`, {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      throw new Error(error instanceof Error ? error.message : "Failed to load campaigns");
    }
  }

  static async getCampaign(id: string): Promise<{
    campaign: {
      id: string;
      title: string;
      status: string;
      time_zone: string;
      calendly_link: string;
      created_at: string;
      updated_at: string;
      value_prop: string;
      proof_point: string;
      pain_point: string;
      sender_info: string;
    };
  }> {
    try {
      logger.info("Fetching campaign with ID:", id);
      const response = await fetch(`${this.BASE_URL}/${id}`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      throw new Error(`Failed to fetch campaign: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async createCampaign(title: string): Promise<{
    id: string;
    title: string;
    status: string;
    time_zone: string;
    sender_info: string;
    value_prop: string;
    pain_point: string;
    proof_point: string;
    updated_at: string;
    created_at: string;
  }> {
    try {
      const response = await fetch(`${this.BASE_URL}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({
          title,
          status: "draft",
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.campaign;
    } catch (error) {
      throw new Error(`Failed to create campaign: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async updateCampaign(data: {
    id: string;
    value_prop: string;
    proof_point: string;
    pain_point: string;
    sender_info: string;
    calendly_link: string;
    title: string;
    status: string;
    time_zone: string;
  }): Promise<void> {
    try {
      logger.info("PUT Updating campaign with data:", data);
      const response = await fetch(`${this.BASE_URL}/${data.id}`, {
        method: "PUT",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify(data),
        credentials: "include",
      });

      logger.info("PUT Response:", response);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      throw new Error(`Failed to update campaign: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async deleteCampaign(id: string): Promise<void> {
    try {
      const response = await fetch(`${this.BASE_URL}/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      throw new Error(`Failed to delete campaign: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async getSequences(campaignId: string) {
    try {
      const response = await fetch(`${this.BASE_URL_CAMPAIGN}/${campaignId}/sequences`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      throw new Error(`Failed to fetch sequences: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async createSequence(
    campaignId: string,
    metaPrompt: string,
    providerId: string,
  ): Promise<{
    sequences: Array<{ id: string; step_order: number; delay_days: number; provider_id: string; campaign_id: string }>;
  }> {
    try {
      const payload = {
        outreach_sequences: [
          {
            meta_prompt: metaPrompt,
            step_order: 1,
            delay_minutes: 0,
            provider_id: providerId,
          },
        ],
      };

      logger.log("Creating sequence with payload:", payload); // Debug log

      const response = await fetch(`${this.BASE_URL_CAMPAIGN}/${campaignId}/sequences`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Server error response:", errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      throw new Error(`Failed to create sequence: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async getLeads(campaignId: string): Promise<{
    leads: {
      id: string;
      first_name: string;
      last_name: string;
      current_step: number;
      campaign_id: string;
      created_at: string;
      updated_at: string;
      work_title: string;
      email: string;
      company_name: string;
    }[];
  }> {
    try {
      const response = await fetch(`${this.BASE_URL_CAMPAIGN}/${campaignId}/leads`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      throw new Error(`Failed to fetch leads: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async addLeadsToCampaign(
    campaignId: string,
    leads: { first_name: string; last_name: string; email: string; work_title: string; company_name: string }[],
  ): Promise<{
    leads: {
      iD: string;
      createdAt: string;
      updatedAt: string;
      firstName: string;
      lastName: string;
      currentStep: number;
      lastMessageSentAt: string;
      nextScheduledStepAt: string;
      campaignID: string;
      searchQuery: string;
      workTitle: string;
      email: string;
      company_name: string;
    }[];
  }> {
    try {
      const response = await fetch(`${this.BASE_URL_CAMPAIGN}/${campaignId}/leads`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({
          search_query: "",
          leads,
        }),
        credentials: "include",
      });
      return response.json();
    } catch (error) {
      console.error("Add leads to campaign error:", error);
      throw new Error(`Failed to add leads to campaign: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async removeLeadFromCampaign(campaignId: string, leadId: string): Promise<{ message: string }> {
    try {
      logger.info(`Removing lead ${leadId} from campaign ${campaignId}`);
      const response = await fetch(`${this.BASE_URL_CAMPAIGN}/${campaignId}/leads/${leadId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error("Remove lead from campaign error:", error);
      throw new Error(
        `Failed to remove lead from campaign: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  static async updateSequence(campaignId: string, sequenceId: string, data: Partial<SequenceAPI>): Promise<void> {
    try {
      const response = await fetch(`${this.BASE_URL_CAMPAIGN}/${campaignId}/sequences/${sequenceId}`, {
        method: "PUT",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({ outreach_sequence_data: data }),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      throw new Error(`Failed to update sequence: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async getMessages(sequenceId: string) {
    try {
      const response = await fetch(`${this.BASE_URL_SEQUENCES}/${sequenceId}/messages`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      throw new Error(`Failed to fetch messages: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async updateMessage(
    sequenceId: string,
    messageId: string,
    data: { message: string; title: string; approved?: boolean },
  ): Promise<void> {
    try {
      // Construct the payload with all required fields
      const payload = {
        data: {
          message: data.message,
          is_approved: !!data.approved,
          title: data.title,
        },
      };

      const response = await fetch(`${this.BASE_URL_SEQUENCES}/${sequenceId}/messages/${messageId}`, {
        method: "PUT",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error("Failed to update message:", error);
      throw new Error(`Failed to update message: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async updateLead(
    campaignId: string,
    leadId: string,
    data: {
      first_name: string;
      last_name: string;
      email: string;
      work_title: string;
      company_name: string;
      current_step: number;
    },
  ): Promise<void> {
    try {
      const payload = {
        data: {
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          work_title: data.work_title,
          company_name: data.company_name,
          current_step: data.current_step,
        },
      };

      const response = await fetch(`${this.BASE_URL_CAMPAIGN}/${campaignId}/leads/${leadId}`, {
        method: "PUT",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      throw new Error(`Failed to update lead: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async releaseCampaign(campaignId: string): Promise<{ message: string }> {
    try {
      const response = await fetch(`${this.BASE_URL}/${campaignId}/release`, {
        method: "PUT",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({}),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      throw new Error(`Failed to release campaign: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async addMessageToSequence(sequenceId: string, message: SequenceMessage): Promise<void> {
    try {
      const response = await fetch(`${this.BASE_URL_SEQUENCES}/${sequenceId}/messages`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify(message),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      throw new Error(`Failed to add message: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }
}
